import { Shield, User, Crown, MoreHorizontal } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Avatar } from '@/components/ui/Avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu'

interface TeamMember {
  id: string
  user_id: string
  email: string
  full_name: string
  avatar_url: string | null
  role: 'owner' | 'admin' | 'agent'
  created_at: string
}

const roleIcons = {
  owner: Crown,
  admin: Shield,
  agent: User,
}

const roleLabels = {
  owner: 'Owner',
  admin: 'Admin',
  agent: 'Agent',
}

interface TeamMemberRowProps {
  member: TeamMember
  canManage: boolean
  onRoleChange: (memberId: string, newRole: 'admin' | 'agent') => void
  onRemove: (memberId: string) => void
}

export function TeamMemberRow({ member, canManage, onRoleChange, onRemove }: TeamMemberRowProps) {
  const RoleIcon = roleIcons[member.role]

  return (
    <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg group">
      <Avatar
        name={member.full_name}
        email={member.email}
        src={member.avatar_url || undefined}
        avatarType={member.avatar_url ? 'custom' : 'gravatar'}
        size="md"
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium truncate">{member.full_name}</span>
          <span className="flex items-center gap-1 text-xs bg-muted px-2 py-0.5 rounded">
            <RoleIcon className="size-3" />
            {roleLabels[member.role]}
          </span>
        </div>
        <div className="text-sm text-muted-foreground truncate">
          {member.email}
        </div>
      </div>
      {canManage && member.role !== 'owner' && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="opacity-0 group-hover:opacity-100 cursor-pointer"
            >
              <MoreHorizontal className="size-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {member.role !== 'admin' && (
              <DropdownMenuItem
                onClick={() => onRoleChange(member.id, 'admin')}
                className="cursor-pointer"
              >
                <Shield className="size-4 mr-2" />
                Make Admin
              </DropdownMenuItem>
            )}
            {member.role !== 'agent' && (
              <DropdownMenuItem
                onClick={() => onRoleChange(member.id, 'agent')}
                className="cursor-pointer"
              >
                <User className="size-4 mr-2" />
                Make Agent
              </DropdownMenuItem>
            )}
            <DropdownMenuItem
              onClick={() => onRemove(member.id)}
              className="cursor-pointer text-destructive focus:text-destructive"
            >
              Remove from team
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  )
}
